import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { computePlayerPoints, PlayerPointsResult } from "@/lib/points";

export interface PlayerGroupPoints extends PlayerPointsResult {
  gamesCounted: number;
  wins: number;
  netProfit: number;
  totalBuyins: number;
}

export interface GroupPointsData {
  byPlayer: Record<string, PlayerGroupPoints>;
  skipped: { gameId: string; playerId: string }[];
}

const PAGE = 1000;
const CHUNK = 100;

const chunk = <T,>(arr: T[], size: number) => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

async function fetchAll<T>(table: "payouts" | "buyins", columns: string, gameIds: string[]): Promise<T[]> {
  const rows: T[] = [];
  for (const ids of chunk(gameIds, CHUNK)) {
    let from = 0;
    while (true) {
      const { data, error } = await supabase
        .from(table)
        .select(columns)
        .in("game_id", ids)
        .order("id")
        .range(from, from + PAGE - 1);
      if (error) throw error;
      rows.push(...((data as unknown as T[]) || []));
      if (!data || data.length < PAGE) break;
      from += PAGE;
    }
  }
  return rows;
}

async function loadGroupPoints(groupId: string): Promise<GroupPointsData> {
  const games: { id: string; created_at: string; settled_at: string | null }[] = [];
  let from = 0;
  while (true) {
    const { data, error } = await supabase
      .from("games")
      .select("id, created_at, settled_at")
      .eq("group_id", groupId)
      .eq("status", "settled")
      .order("id")
      .range(from, from + PAGE - 1);
    if (error) throw error;
    games.push(...(data || []));
    if (!data || data.length < PAGE) break;
    from += PAGE;
  }

  const time = (s: string) => new Date(s).getTime();
  games.sort((a, b) =>
    time(a.settled_at ?? a.created_at) - time(b.settled_at ?? b.created_at) ||
    time(a.created_at) - time(b.created_at) ||
    a.id.localeCompare(b.id)
  );
  const order = new Map(games.map((g, i) => [g.id, i]));
  const gameIds = games.map((g) => g.id);
  if (gameIds.length === 0) return { byPlayer: {}, skipped: [] };

  const [payouts, buyins] = await Promise.all([
    fetchAll<{ game_id: string; player_id: string; net_amount: number }>("payouts", "id, game_id, player_id, net_amount", gameIds),
    fetchAll<{ game_id: string; player_id: string; amount: number }>("buyins", "id, game_id, player_id, amount", gameIds),
  ]);

  const key = (p: string, g: string) => `${p}|${g}`;
  const payoutMap = new Map<string, number>();
  for (const p of payouts) payoutMap.set(key(p.player_id, p.game_id), Number(p.net_amount) || 0);
  const buyinMap = new Map<string, number>();
  for (const b of buyins) {
    const k = key(b.player_id, b.game_id);
    buyinMap.set(k, (buyinMap.get(k) || 0) + (Number(b.amount) || 0));
  }

  const perPlayer = new Map<string, { gameId: string; net: number; buyin: number }[]>();
  const skipped: GroupPointsData["skipped"] = [];
  for (const [k, buyin] of buyinMap) {
    const [playerId, gameId] = k.split("|");
    const net = payoutMap.get(k);
    if (net === undefined) {
      console.warn(`[points] Skipping settled game ${gameId} for player ${playerId}: buy-in exists but payout missing`);
      skipped.push({ gameId, playerId });
      continue;
    }
    if (!perPlayer.has(playerId)) perPlayer.set(playerId, []);
    perPlayer.get(playerId)!.push({ gameId, net, buyin });
  }

  const byPlayer: Record<string, PlayerGroupPoints> = {};
  for (const [playerId, list] of perPlayer) {
    list.sort((a, b) => order.get(a.gameId)! - order.get(b.gameId)!);
    const res = computePlayerPoints(list);
    byPlayer[playerId] = {
      ...res,
      gamesCounted: list.length,
      wins: list.filter((r) => Math.round(r.net * 100) / 100 > 0).length,
      netProfit: list.reduce((s, r) => s + r.net, 0),
      totalBuyins: list.reduce((s, r) => s + r.buyin, 0),
    };
  }
  return { byPlayer, skipped };
}

export const useGroupPoints = (groupId?: string, refreshKey?: unknown) =>
  useQuery({
    queryKey: ["groupPoints", groupId, refreshKey],
    queryFn: () => loadGroupPoints(groupId!),
    enabled: !!groupId,
    staleTime: 0,
  });
